import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import MaterialTimePicker from './MaterialTimePicker';
import FormDropdown from '../../../components/common/FormDropdown';
import './CreateGroundModal.scss';

const DAY_OPTIONS = [
  { value: '', label: 'Select Days' },
  { value: 'Monday', label: 'Monday' },
  { value: 'Tuesday', label: 'Tuesday' },
  { value: 'Wednesday', label: 'Wednesday' },
  { value: 'Thursday', label: 'Thursday' },
  { value: 'Friday', label: 'Friday' },
  { value: 'Saturday', label: 'Saturday' },
  { value: 'Sunday', label: 'Sunday' },
];

export interface GroundFormData {
  name: string;
  location: string;
  email: string;
  contactNumber: string;
  fromTime: string;
  toTime: string;
  selectDays: string;
  description: string;
  gmapLink: string;
  groundFacilities: string;
}

interface CreateGroundModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: GroundFormData) => void | Promise<void>;
  initialData?: Partial<GroundFormData>;
  title?: string;
  submitLabel?: string;
  description?: string;
}

const DEFAULT_FORM: GroundFormData = {
  name: '',
  location: '',
  email: '',
  contactNumber: '',
  fromTime: '',
  toTime: '',
  selectDays: '',
  description: '',
  gmapLink: '',
  groundFacilities: '',
};

function CreateGroundModal({
  open,
  onOpenChange,
  onSubmit,
  initialData,
  title = 'Add Ground',
  submitLabel = 'Add Ground',
  description = 'Manually add your cricket grounds to set up perfect matches. Ensure all details are accurate for scheduling!',
}: CreateGroundModalProps) {
  const [form, setForm] = useState<GroundFormData>({
    ...DEFAULT_FORM,
    ...initialData,
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...DEFAULT_FORM, ...initialData } as GroundFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit(form);
      setForm({
        name: '',
        location: '',
        email: '',
        contactNumber: '',
        fromTime: '',
        toTime: '',
        selectDays: '',
        description: '',
        gmapLink: '',
        groundFacilities: '',
      });
      onOpenChange(false);
    } catch {
      // Keep the modal open so the user can fix the form or retry.
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (open) {
      document.addEventListener('keydown', onEscape);
    }
    return () => document.removeEventListener('keydown', onEscape);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="create-ground-modal-overlay"
      onClick={handleClose}
      aria-modal="true"
      role="dialog"
    >
      <div
        className="create-ground-modal"
        onClick={(e) => e.stopPropagation()}
        aria-labelledby="create-ground-modal-title"
      >
        <div className="create-ground-modal-header">
          <div className="create-ground-modal-header-row">
            <h2 id="create-ground-modal-title" className="create-ground-modal-title">
              {title}
            </h2>
            <button
              type="button"
              className="create-ground-modal-close"
              onClick={handleClose}
              aria-label="Close"
            >
              &#10005;
            </button>
          </div>
          <p className="create-ground-modal-description">
            {description}
          </p>
        </div>

        <div className="create-ground-modal-body">
          <form className="create-ground-form" onSubmit={handleSubmit}>
            <div className="create-ground-grid">
              <div className="create-ground-col">
                <div className="create-ground-field">
                  <label>Ground Name<span className="req">*</span></label>
                  <input
                    type="text"
                    placeholder="Enter Ground Name"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="create-ground-field">
                  <label>E-Mail<span className="req">*</span></label>
                  <input
                    type="email"
                    placeholder="Enter Email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    required
                  />
                </div>
                <div className="create-ground-field">
                  <label>From Time<span className="req">*</span></label>
                  <MaterialTimePicker
                    value={form.fromTime}
                    onChange={(v) => setForm((p) => ({ ...p, fromTime: v }))}
                    placeholder="Select time"
                  />
                </div>
                <div className="create-ground-field">
                  <label>Description<span className="req">*</span></label>
                  <textarea
                    rows={4}
                    placeholder="Enter Description"
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    required
                  />
                </div>
                <div className="create-ground-field">
                  <label>Google Map Link</label>
                  <input
                    type="url"
                    placeholder="https://www.google.com/maps"
                    value={form.gmapLink}
                    onChange={(e) => setForm((p) => ({ ...p, gmapLink: e.target.value }))}
                  />
                </div>
              </div>
              <div className="create-ground-col">
                <div className="create-ground-field">
                  <label>Location<span className="req">*</span></label>
                  <input
                    type="text"
                    placeholder="Enter Location"
                    value={form.location}
                    onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                    required
                  />
                </div>
                <div className="create-ground-field">
                  <label>Mobile Number</label>
                  <PhoneInput
                    country="in"
                    value={form.contactNumber}
                    onChange={(val) => setForm((p) => ({ ...p, contactNumber: val }))}
                    inputClass="create-ground-phone-input"
                    containerClass="create-ground-phone-wrap"
                    placeholder="9876543210"
                    enableSearch
                  />
                </div>
                <div className="create-ground-field">
                  <label>To Time<span className="req">*</span></label>
                  <MaterialTimePicker
                    value={form.toTime}
                    onChange={(v) => setForm((p) => ({ ...p, toTime: v }))}
                    placeholder="Select time"
                  />
                </div>
                <div className="create-ground-field">
                  <label>Select Days</label>
                  <FormDropdown
                    placeholder="Select Days"
                    value={form.selectDays}
                    onChange={(v) => setForm((p) => ({ ...p, selectDays: v }))}
                    options={DAY_OPTIONS.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))}
                  />
                </div>
                <div className="create-ground-field">
                  <label>Ground Facilities</label>
                  <input
                    type="text"
                    placeholder="e.g. Dressing room, Parking"
                    value={form.groundFacilities}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, groundFacilities: e.target.value }))
                    }
                  />
                </div>
              </div>
            </div>
            <div className="create-ground-actions">
              <button
                type="button"
                className="create-ground-btn-outline"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button type="submit" className="create-ground-btn-primary" disabled={submitting}>
                {submitting ? `${submitLabel.replace(/\s+$/, '')}…` : submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default CreateGroundModal;
